// Overdrive aura (owner 2026-11-01: 'power up for a short period of time, but you lose health… There should be an
// obvious aura around the player when it's happening'). Render-only, driven by Sim.odT. Deliberately NOT the flowing
// Surge flame: a tight scarlet shell that THROBS like a heartbeat (double beat), with a hot edge, a pulsing ground ring,
// rising embers and a red light that beats with it — so Overdrive and Surge II read as different things at a glance.
import * as THREE from 'three/webgpu';
import { abs, color, dot, float, mix, mx_noise_float, normalView, oneMinus, positionLocal, positionViewDirection, pow, smoothstep, time, uniform, vec3 } from 'three/tsl';
import type { Fx } from './fx';

const OD_BODY = 0xff2a12, OD_HOT = 0xff8a3a;

export class OverdriveAura {
  readonly shell: THREE.Mesh;
  readonly ring: THREE.Mesh;
  private k = 0;            // fade 0..1
  private beatT = 0;        // heartbeat phase (s)
  private strength = uniform(0);
  private emberAcc = 0;
  private flashed = false;

  constructor(scene: THREE.Scene, private quality: string) {
    // tall teardrop shell: narrow top, full hips, bright fresnel edge, flame tongues scrolling upward; transparent centre
    const geo = new THREE.SphereGeometry(1, 32, 22);
    const fp = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < fp.count; i++) {
      const y = fp.getY(i), k = y > 0 ? 1 - y * 0.5 : 1 - Math.max(0, -y - 0.6) * 0.8;
      fp.setXYZ(i, fp.getX(i) * k, y > 0 ? y * 1.35 : y * 0.9, fp.getZ(i) * k);
    }
    geo.computeVertexNormals();
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false }); // normal blending: scarlet stays scarlet over bright grass
    const fres = pow(oneMinus(abs(dot(normalView, positionViewDirection))), float(1.5));
    const n = mx_noise_float(vec3(positionLocal.x.mul(3.2), positionLocal.y.mul(1.8).sub(time.mul(5.5)), positionLocal.z.mul(3.2))).mul(0.5).add(0.5);
    const tongues = smoothstep(float(0.42), float(0.8), n.add(positionLocal.y.mul(0.3)));
    const s = this.strength;
    m.colorNode = mix(color(OD_BODY), color(OD_HOT), fres.mul(fres).mul(0.8).add(tongues.mul(0.35)).min(1)).mul(s.mul(1.3).add(0.5));
    // centre nearly clear (hero stays visible), edges bright, tongues add licking highlights
    m.opacityNode = fres.mul(0.9).add(tongues.mul(0.35)).add(0.08).mul(s).min(0.92);
    this.shell = new THREE.Mesh(geo, m); this.shell.visible = false; this.shell.renderOrder = 3; this.shell.frustumCulled = false;
    const rg = new THREE.RingGeometry(0.7, 1, 48).rotateX(-Math.PI / 2);
    const rm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false, side: THREE.DoubleSide });
    rm.colorNode = mix(color(OD_BODY), color(OD_HOT), s.mul(0.6)).mul(1.3);
    rm.opacityNode = s.mul(0.85);
    this.ring = new THREE.Mesh(rg, rm); this.ring.visible = false; this.ring.renderOrder = 2; this.ring.frustumCulled = false;
    scene.add(this.shell, this.ring);
  }

  /** odT = seconds of Overdrive left (0 = off). Call every frame with the hero's feet position. */
  update(hx: number, gy: number, hz: number, odT: number, dt: number, fx: Fx, reducedFlash: boolean): void {
    const want = odT > 0 ? 1 : 0;
    this.k += (want - this.k) * Math.min(1, dt * (want ? 9 : 5));
    if (this.k < 0.02 && !want) { this.shell.visible = this.ring.visible = false; this.k = 0; return; }
    this.shell.visible = this.ring.visible = true;
    // heartbeat: lub-dub every 0.7 s (two bumps), the second softer; shell and ring swell with it
    this.beatT = (this.beatT + dt) % 0.7;
    const b1 = Math.exp(-this.beatT * 14), b2 = this.beatT > 0.22 ? Math.exp(-(this.beatT - 0.22) * 14) * 0.6 : 0;
    const beat = Math.min(1, b1 + b2);
    const strength = this.k * (0.55 + beat * 0.45);
    this.strength.value = strength;
    const r = 1.9 + beat * 0.3;
    this.shell.position.set(hx, gy + 1.15, hz); this.shell.scale.set(r, 1.7 + beat * 0.2, r);
    this.shell.rotation.y += dt * 0.8;
    this.ring.position.set(hx, gy + 0.06, hz); this.ring.scale.setScalar(2.3 + beat * 0.7);
    // red beat light (skipped on Reduced Flash: the shell and ring still show the state)
    if (this.beatT < 0.03 && !this.flashed) { this.flashed = true; if (!reducedFlash) fx.lights.flash(hx, gy + 1.2, hz, OD_BODY, 26 * this.k, 0.22, 11); }
    if (this.beatT > 0.1) this.flashed = false;
    // rising embers off the body, denser on the beat
    const rate = (this.quality === 'low' ? 14 : this.quality === 'medium' ? 26 : 40) * this.k * (0.6 + beat);
    this.emberAcc += rate * dt;
    while (this.emberAcc >= 1) {
      this.emberAcc -= 1;
      const a = Math.random() * Math.PI * 2, d = 0.35 + Math.random() * 0.75;
      fx.particles.emitDir(hx + Math.cos(a) * d, gy + 0.1 + Math.random() * 1.6, hz + Math.sin(a) * d, (Math.random() - 0.5) * 0.8, 2.5 + Math.random() * 3, (Math.random() - 0.5) * 0.8, Math.random() < 0.3 ? OD_HOT : OD_BODY, 0.22 + Math.random() * 0.16, 0.5 + Math.random() * 0.4, 3);
    }
  }
}
